/* Copyright (c) 2020-present CloudIO Inc */

import {
  selector,
  useStateX,
  useStateXAtomValue,
  useStateXGetter,
  useStateXSelector,
  useStateXSetter,
  useStateXValue,
} from '@cloudio-saas/statex';
import stringify from 'json-stable-stringify';
import { makeStyles } from '@mui/styles';
import { useRowIndex } from 'common/Context';
import {
  BaseComponentItem,
  ComponentItemData,
  ComponentItemType,
  getManager,
  ItemID,
  ItemType,
  RequiredItem,
} from 'componentregistry';
import { useCurrentDataStore } from 'components/DataStoreReference/DataStoreReference';
import {
  designPath,
  itemAtom,
  itemTypeAtom,
  runtimeItemAtom,
  runtimePath,
} from 'designer/atoms';
import { propertySelector, visibleChildrenSelector } from 'designer/state';
import useMounted from 'hooks/useMounted';
import { getDefaultValues } from 'layout/Sidebar/panels/PropertiesView/property-editors/PropertyEditorUtils';
import { PlatformContext } from 'platform/PlatformProvider';
import { useCallback, useContext, useMemo, useState } from 'react';
import { itemWithDefaultValues } from 'registry/Components';
import { NOT_DEFINED } from 'utils/isEmpty';

function useStringProperty({
  appUid,
  itemId,
  pageId,
  prop,
}: {
  appUid: string;
  itemId: string;
  pageId: ItemID;
  prop: string;
}): string {
  return (
    (useStateXSelector(propertySelector, {
      params: { appUid, prop, pageId, itemId },
    }) as string) ?? ''
  );
}

function useBooleanProperty({
  appUid,
  itemId,
  pageId,
  prop,
}: {
  appUid: string;
  itemId: string;
  pageId: ItemID;
  prop: string;
}): boolean {
  return (
    (useStateXSelector(propertySelector, {
      params: { appUid, prop, pageId, itemId },
    }) as boolean) ?? false
  );
}

function useRuntimePropertySetter<T>({
  appUid,
  itemId,
  pageId,
  prop,
}: {
  appUid: string;
  itemId: string;
  pageId: ItemID;
  prop: string;
}) {
  const set = useStateXSetter();
  return useCallback(
    (value: T) => {
      set(runtimePath({ appUid, pageId, itemId, key: prop }), value);
    },
    [appUid, itemId, pageId, prop, set],
  );
}

function useRuntimeProperty<T>({
  appUid,
  itemId,
  pageId,
  prop,
  defaultValue,
}: {
  appUid: string;
  itemId: string;
  pageId: ItemID;
  prop: string;
  defaultValue: T;
}) {
  return useStateX<T>(
    runtimePath({ appUid, pageId, itemId, key: prop }),
    defaultValue,
  );
}

function useNumberProperty({
  appUid,
  itemId,
  pageId,
  prop,
}: {
  appUid: string;
  itemId: string;
  pageId: ItemID;
  prop: string;
}) {
  const get = useStateXGetter();
  const value = useStateXSelector(propertySelector, {
    params: { appUid, prop, pageId, itemId },
  }) as number;
  if (value === undefined || value === null) {
    const type = get(itemTypeAtom, {
      params: { appUid, pageId, itemId },
    });
    const mgr = getManager(type);
    return getDefaultValues<number>(mgr, prop);
  }
  return value;
}

function usePlatform() {
  return useContext(PlatformContext);
}

function useParentItemId(appUid: string, pageId: string, itemId: string) {
  return useStateXValue<unknown>(
    designPath({ appUid, pageId, itemId, key: 'parent' }),
    undefined,
  ) as string;
}

function useVisibleChildren(
  appUid: string,
  pageId: string,
  itemId: string,
): { children: ItemID[]; childrenTypes: ItemType[] } {
  const platform = usePlatform();
  const rowIndex = useRowIndex();
  const store = useCurrentDataStore();
  return useStateXSelector(visibleChildrenSelector, {
    params: { appUid, pageId, itemId, rowIndex: `r-${rowIndex ?? -1}` },
    props: { platform, rowIndex, dsAlias: store?.alias ?? NOT_DEFINED },
  });
}

function useVisibleChildrenOfType(
  appUid: string,
  pageId: string,
  itemId: string,
  itemType: ItemType,
): { children: ItemID[]; childrenTypes: ItemType[] } {
  const platform = usePlatform();
  const rowIndex = useRowIndex();
  const store = useCurrentDataStore();
  return useStateXSelector(visibleChildrenSelector, {
    params: { appUid, pageId, itemId, rowIndex: `r-${rowIndex}` },
    props: {
      platform,
      rowIndex,
      dsAlias: store?.alias ?? NOT_DEFINED,
      itemType,
    },
  });
}

const itemsSelector = selector<
  RequiredItem<ItemType>[],
  { ids: ItemID[]; appUid: string; pageId: string }
>({
  path: ['useItems', ':appUid', ':pageId', ':itemIds'],
  get: ({ get }, { ids, appUid, pageId }) => {
    return ids
      .map((id) => {
        const item = get(itemAtom, { params: { appUid, pageId, itemId: id } });
        if (!item) {
          return null;
        }
        return itemWithDefaultValues(item);
      })
      .filter((item) => !!item) as RequiredItem<ItemType>[];
  },
});

function useItems<T extends ItemType>(
  appUid: string,
  pageId: string,
  ids: ItemID[],
): RequiredItem<T>[] {
  return useStateXSelector(itemsSelector, {
    props: { ids, appUid, pageId },
    params: { appUid, pageId, itemIds: stringify(ids) },
  }) as unknown as RequiredItem<T>[];
}

export function useItem<T extends ItemType>(
  appUid: string,
  pageId: string,
  itemId: ItemID,
): RequiredItem<T> {
  const item = useStateXAtomValue(itemAtom, {
    params: { appUid, pageId, itemId },
  });
  if (!item) {
    throw Error(`Item ${itemId} not found!`);
  }
  return useMemo(() => itemWithDefaultValues(item), [item]);
}

function useVisibleSiblings(appUid: string, pageId: string, itemId: string) {
  const parentItemId = useParentItemId(appUid, pageId, itemId);
  return useVisibleChildren(appUid, pageId, parentItemId);
}

const EMPTY_OBJECT = {};

function useParentPropsOfType<T extends ComponentItemType>({
  appUid,
  pageId,
  itemId,
  filter,
}: {
  appUid: string;
  pageId: string;
  itemId: string;
  filter: (parent: BaseComponentItem) => boolean;
}): ComponentItemData<T> {
  const platform = usePlatform();
  const parent = platform.findParent(appUid, pageId, itemId, filter);
  const mgr = parent ? getManager(parent.type) : null;
  const defaultProps = mgr
    ? mgr.defaultValue
    : (EMPTY_OBJECT as ComponentItemData<T>);
  const runtimeProps = useStateXAtomValue(runtimeItemAtom, {
    params: { appUid, pageId, itemId: parent ? parent.itemId : '_dummy_' },
  });
  const designProps = useStateXValue(
    designPath({
      appUid,
      pageId,
      itemId: parent ? parent.itemId : '_dummy_',
      key: 'props',
    }),
    EMPTY_OBJECT as ComponentItemData<T>,
  );
  return { ...defaultProps, ...designProps, ...runtimeProps };
}

function useItemProps<T extends ComponentItemType>({
  appUid,
  pageId,
  itemId,
}: {
  appUid: string;
  pageId: string;
  itemId: string;
}): ComponentItemData<T> {
  const platform = usePlatform();
  const item = platform.getItem(appUid, pageId, itemId);
  const mgr = item ? getManager(item.type) : null;
  const defaultProps = mgr
    ? mgr.defaultValue
    : (EMPTY_OBJECT as ComponentItemData<T>);
  const runtimeProps = useStateXAtomValue(runtimeItemAtom, {
    params: { appUid, pageId, itemId: item ? item.itemId : '_dummy_' },
  });
  const designProps = useStateXValue(
    designPath({
      appUid,
      pageId,
      itemId: item ? item.itemId : '_dummy_',
      key: 'props',
    }),
    EMPTY_OBJECT as ComponentItemData<T>,
  );
  return { ...defaultProps, ...designProps, ...runtimeProps };
}

const useStyles = makeStyles(() => ({
  clickable: {
    cursor: 'pointer',
  },
}));

function useClickableClassName(): string {
  return useStyles().clickable;
}

function usePending(): [boolean, (pending: boolean) => void] {
  const [isPending, _setPending] = useState(false);
  const mounted = useMounted();
  const setPending = useCallback(
    (pending: boolean): void => {
      if (mounted.current) {
        _setPending(pending);
      }
    },
    [mounted],
  );
  return [isPending, setPending];
}

export {
  useBooleanProperty,
  useClickableClassName,
  useItemProps,
  useItems,
  useNumberProperty,
  useParentItemId,
  useParentPropsOfType,
  usePending,
  usePlatform,
  useRuntimeProperty,
  useRuntimePropertySetter,
  useStringProperty,
  useStyles,
  useVisibleChildren,
  useVisibleChildrenOfType,
  useVisibleSiblings,
};
